import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Plus, Trash2, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, StatusBadge, EmptyState } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api, errMsg, fmtINR } from "../../lib/api";

const FIELD_TYPES = ["text", "textarea", "number", "date", "dropdown", "file"];

export default function ServicesConfig() {
  const { code } = useParams();
  const [services, setServices] = useState(null);
  const [editing, setEditing] = useState(null);
  const [schema, setSchema] = useState([]);
  const [stages, setStages] = useState("");
  const [fee, setFee] = useState(0);
  const [sla, setSla] = useState(7);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/t/${code}/services`).then((r) => setServices(r.data)), [code]);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const openEdit = (svc) => {
    setEditing(svc);
    setSchema((svc.form_schema || []).map((f) => ({ ...f, options: f.options || [] })));
    setStages((svc.workflow_stages || []).join(", "));
    setFee((svc.fee_structure || {}).base_fee || 0);
    setSla(svc.sla_days || 7);
  };

  const addField = () => setSchema([...schema, { field_name: "", label: "", type: "text", required: false, order: schema.length + 1, options: [] }]);
  const setField = (i, k, v) => {
    const s = [...schema];
    s[i] = { ...s[i], [k]: v };
    if (k === "label" && !s[i].field_name_locked) s[i].field_name = String(v).toLowerCase().replace(/[^a-z0-9]+/g, "_");
    setSchema(s);
  };

  const save = async (goLive) => {
    setBusy(true);
    try {
      const body = {
        form_schema: schema.filter((f) => f.label),
        workflow_stages: stages.split(",").map((s) => s.trim()).filter(Boolean),
        fee_structure: { base_fee: Number(fee) || 0 },
        sla_days: Number(sla) || 7,
      };
      if (goLive !== undefined) body.is_live = goLive;
      await api.put(`/t/${code}/services/${editing.id}`, body);
      toast.success(goLive ? "Service published live" : "Service configuration saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (!services) return <Spinner />;

  return (
    <div>
      <PageHeader title="Service Configuration" subtitle="Configure forms, workflows and fees for assigned services" />
      {services.length === 0 ? (
        <EmptyState title="No services assigned" subtitle="Ask the platform Super Admin to assign services to this municipality" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="services-config-grid">
          {services.map((s) => (
            <Card key={s.id} className="shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-sm">{s.service_name}</p>
                    <p className="text-xs text-muted-foreground font-mono">{s.service_code}</p>
                  </div>
                  <StatusBadge status={s.is_live ? "live" : "inactive"} />
                </div>
                <div className="flex gap-2 mt-3 flex-wrap">
                  <Badge variant="secondary" className="text-[10px]">{(s.form_schema || []).length} fields</Badge>
                  <Badge variant="secondary" className="text-[10px]">{(s.workflow_stages || []).length} stages</Badge>
                  <Badge variant="secondary" className="text-[10px]">Fee {fmtINR((s.fee_structure || {}).base_fee)}</Badge>
                  <Badge variant="secondary" className="text-[10px]">SLA {s.sla_days}d</Badge>
                </div>
                <Button variant="outline" size="sm" className="w-full mt-3" onClick={() => openEdit(s)} data-testid={`configure-service-${s.service_code}`}>
                  <Settings2 className="h-3.5 w-3.5 mr-2" /> Configure
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing?.service_name}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Form fields</Label>
                  <Button variant="outline" size="sm" onClick={addField} data-testid="service-add-field-button"><Plus className="h-3.5 w-3.5 mr-1" /> Field</Button>
                </div>
                <div className="space-y-2">
                  {schema.map((f, i) => (
                    <div key={i} className="border rounded-lg p-2.5 grid grid-cols-12 gap-2 items-center">
                      <Input className="col-span-4" placeholder="Label" value={f.label} onChange={(e) => setField(i, "label", e.target.value)} />
                      <Select value={f.type} onValueChange={(v) => setField(i, "type", v)}>
                        <SelectTrigger className="col-span-3"><SelectValue /></SelectTrigger>
                        <SelectContent>{FIELD_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                      {f.type === "dropdown" ? (
                        <Input className="col-span-3" placeholder="Options (comma sep)" value={(f.options || []).join(",")}
                          onChange={(e) => setField(i, "options", e.target.value.split(",").map((x) => x.trim()))} />
                      ) : <div className="col-span-3" />}
                      <label className="col-span-1 flex items-center gap-1 text-xs">
                        <Switch checked={!!f.required} onCheckedChange={(v) => setField(i, "required", v)} />
                      </label>
                      <Button variant="ghost" size="icon" className="col-span-1 h-8 w-8" onClick={() => setSchema(schema.filter((_, j) => j !== i))}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  ))}
                  {schema.length === 0 && <p className="text-sm text-muted-foreground text-center py-3">No fields yet. Add form fields to enable applications.</p>}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1 sm:col-span-3">
                  <Label>Workflow stages (comma separated)</Label>
                  <Input value={stages} onChange={(e) => setStages(e.target.value)} data-testid="service-stages-input" />
                </div>
                <div className="space-y-1">
                  <Label>Base fee (INR)</Label>
                  <Input type="number" value={fee} onChange={(e) => setFee(e.target.value)} data-testid="service-fee-input" />
                </div>
                <div className="space-y-1">
                  <Label>SLA (days)</Label>
                  <Input type="number" value={sla} onChange={(e) => setSla(e.target.value)} />
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => save()} disabled={busy} data-testid="service-save-button">Save draft</Button>
                {editing.is_live ? (
                  <Button variant="destructive" onClick={() => save(false)} disabled={busy} data-testid="service-unpublish-button">Take offline</Button>
                ) : (
                  <Button onClick={() => save(true)} disabled={busy} data-testid="service-golive-button">Save &amp; Go Live</Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
